import { AxiosInstance, AxiosRequestConfig } from 'axios'
import Debug from 'debug'
import { z } from 'zod'
import { isZodError } from '~/types/typeUtils'
import type { useAuthStore } from '~/stores/auth.store'
import { getDeviceData, type DeviceData } from '~/utils/deviceUtils'

type AuthStore = ReturnType<typeof useAuthStore>

interface ServiceRequestConfig extends AxiosRequestConfig {
  _retry?: boolean
}

export abstract class BaseService {
  abstract readonly client: AxiosInstance
  protected debug: Debug.Debugger = Debug('app:base-service')
  private static refreshPromise: Promise<string | null> | null = null

  protected async request<T extends z.ZodType>(
    config: ServiceRequestConfig,
    schema: T
  ): Promise<z.infer<T>> {
    try {
      const res = await this.client(config)
      return await schema.parseAsync(res.data)
    } catch (error: any) {
      if (isZodError(error)) {
        this.debug(`ZodError on request -> ${config.url ?? ''}`, error)
      } else if (this.shouldAttemptRefresh(error, config)) {
        return await this.handleUnauthorizedError(config, schema, error)
      }

      throw error
    }
  }

  private shouldAttemptRefresh(error: any, config: ServiceRequestConfig): boolean {
    const shouldRefresh =
      error.response?.status === 401 &&
      !config._retry &&
      !config.url?.includes('/refresh-token') &&
      !!(config.headers?.authorization || config.headers?.Authorization)
    return shouldRefresh
  }

  private async handleUnauthorizedError<T extends z.ZodType>(
    config: ServiceRequestConfig,
    schema: T,
    originalError: any
  ): Promise<z.infer<T>> {
    if (import.meta.server) {
      throw new Error('Cannot refresh token on server')
    }

    const authStore = await import('~/stores/auth.store').then(m => m.useAuthStore())
    try {
      if (!authStore.token) {
        throw originalError
      }
      const contentStore = await import('~/stores/content.store').then(m => m.useContentStore())
      const ssoUrl = contentStore.site?.ssoUrl ?? ''
      const deviceData = await getDeviceData()
      this.debug(
        `Refreshing token for ${config.url ?? ''} with device identifier ${deviceData.deviceIdentifier}`
      )

      // Handle concurrent refreshes using a static promise
      BaseService.refreshPromise ??= this.performTokenRefresh(authStore, {
        ssoUrl,
        deviceData,
      })

      const accessToken = await BaseService.refreshPromise

      if (!accessToken) {
        throw originalError
      }

      const configRetry = {
        ...config,
        headers: { ...config.headers, Authorization: `Bearer ${accessToken}` },
        _retry: true,
      }
      this.debug(`Retrying request with new token for ${configRetry.url ?? ''}`, configRetry)

      // Retry the original request with the new token and avoid further retries
      return await this.request(configRetry, schema)
    } catch (currentError: any) {
      if (currentError.response?.status === 401) {
        this.debug(`401 on refresh token -> ${config.url ?? ''} will logout`)
        // 401 - logout, throw original error
        await authStore.removeToken(true)
        throw originalError
      }

      this.debug(`Error on refresh token -> ${config.url ?? ''}`, currentError)
      throw currentError
    }
  }

  private async performTokenRefresh(
    authStore: AuthStore,
    { ssoUrl, deviceData }: { ssoUrl: string; deviceData: DeviceData }
  ): Promise<string | null> {
    const requestFetch = useRequestFetch()

    try {
      const { accessToken } = await requestFetch<{ accessToken: string }>(
        '/api/auth/refresh-token',
        {
          method: 'POST',
          body: { ssoUrl, ...deviceData },
        }
      )
      authStore.setToken(accessToken)
      return accessToken
    } catch {
      return null
    } finally {
      BaseService.refreshPromise = null
    }
  }
}
